import { NgTemplateOutlet } from "@angular/common";
import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  NbAlertModule,
  NbCardModule,
  NbIconModule,
  NbMediaBreakpointsService,
  NbPopoverModule,
  NbThemeService,
  NbTooltipModule,
  NbUserModule,
} from "@nebular/theme";
import { DirectoryService } from "app/@core/services/directory.service";
import { UserService } from "app/@core/services/users.service";
import {
  Course,
  Directory,
  DirectoryFilter,
  Industry,
  Masterclass,
} from "app/@core/types";
import { DayjsService } from "app/@core/utils/dayjs.service";
import { FilterByPipe, NgPipesModule, ShufflePipe } from "ngx-pipes";
import { map } from "rxjs";
import { LineTruncationLibModule } from "../../modules/line-truncation/line-truncation.module";
import { SanitizeHtmlPipe } from "../../pipes";
import { HighlightPipe } from "../../pipes/highlight.pipe";
import { TruncateObjectPipe } from "../../pipes/truncateobject.pipe";
import { PlaceholderComponent } from "../placeholder/placeholder.component";

@Component({
  standalone: true,
  selector: "ngx-peeps-find",
  styleUrls: ["./peeps.component.scss"],
  templateUrl: "./peeps.component.html",
  imports: [
    NbAlertModule,
    NbCardModule,
    NbUserModule,
    NbIconModule,
    NbTooltipModule,
    NbPopoverModule,
    PlaceholderComponent,
    SanitizeHtmlPipe,
    LineTruncationLibModule,
    NgPipesModule,
    TruncateObjectPipe,
    HighlightPipe,
    NgTemplateOutlet,
  ],
  providers: [ShufflePipe, FilterByPipe],
})
export class PeepsFindComponent implements OnInit {
  private router = inject(Router);
  private themeService = inject(NbThemeService);
  private breakpointService = inject(NbMediaBreakpointsService);
  private userService = inject(UserService);
  private directoryService = inject(DirectoryService);
  private shufflePipe = inject(ShufflePipe);
  private dayjs = inject(DayjsService);

  heading = input<string>();
  course = input.required<Course>();
  style = input<string>();
  col = input<number>(1);
  showcount = input<string>();
  filter = input<DirectoryFilter>({
    key: "course_id",
    value: 1,
    limit: 2,
    truncate: 3,
  });
  search = input<string>("");

  isSm: boolean = false;

  count: number = 0;
  industry: Industry;
  industries: Industry[] = this.userService.getIndustries();

  // searchField: string;
  activedirectory: string = "all";

  searchField = signal<string>("");

  constructor() {
    effect(() => {
      const course = this.course();
      if (course.id)
        untracked(() => this.directoryService.loadCourseDirectory(course.id));
    });
  }

  ngOnInit() {
    // this.directoryService.loadCourseDirectory(this.course().id);
    const { sm } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(map(([, currentBreakpoint]) => currentBreakpoint.width < sm))
      .subscribe((isLessThanSm: boolean) => (this.isSm = !isLessThanSm));
  }

  readonly filteredPeeps = computed(() => {
    const rawData = [...this.directoryService.peeps()];
    const currentFilter = this.filter();
    const currentSearch = this.search();
    let result: Directory[] = [];

    // Apply Category Filtering
    switch (currentFilter.key) {
      case "industry_id":
        result = rawData.filter((p) => p.industry_id === currentFilter.value);
        break;
      case "timezone":
        result = rawData.filter((p) => p.timezone === currentFilter.value);
        break;
      case "new_member":
        result = rawData.sort((a, b) =>
          this.dayjs.compareDesc(b.created, a.created),
        );
        break;
      case "masterclass":
        result = rawData.filter((p) =>
          p.masterclasses.some(
            (m: Masterclass) => m.id === currentFilter.value,
          ),
        );
        break;
      case "random":
        result = this.shufflePipe.transform(rawData);
        break;
      case "similar_interest":
        // Assuming 'course' is also a signal or accessible property
        const ownedMasterclasses = this.course()
          .masterclasses.filter((m) => m.status === 1 && m.is_owned === 1)
          .map((m) => m.id);

        result = rawData.filter(
          (p) =>
            p.industry_id === currentFilter.value ||
            p.masterclasses.some((m: Masterclass) =>
              ownedMasterclasses.includes(m.id),
            ),
        );
        break;
      default:
        result = rawData;
    }
    //Apply Search (Replaces FilterByPipe for better performance)
    if (currentSearch) {
      result = result.filter((p) =>
        ["name", "email", "company", "industry", "timezone"].some((key) =>
          String(p[key as keyof Directory] ?? "")
            .toLowerCase()
            .includes(currentSearch),
        ),
      );
    }

    // Handle Pagination/Limits
    if (currentFilter.limit !== -1) {
      const limit = !this.isSm ? currentFilter.truncate : currentFilter.limit;
      // We update a side-effect signal if needed for "remaining count"
      // (Note: Senior tip: calculation happens here, count is used in UI)
      return result.slice(0, limit);
    }

    return result;
  });

  readonly remainingCount = computed(() => {
    const total = this.directoryService.peeps().length;
    const shown = this.filteredPeeps().length;
    return total - shown;
  });

  getArray(limit: number): number[] {
    return Array.from({ length: limit }, (_, i) => i);
  }

  getStateCountry(data: Directory): string {
    let sc = [data.state, data.country];
    let scs = sc.filter(function (val) {
      return val != "";
    });
    return scs.length > 0 ? scs.join(", ") : "None";
  }

  goToPage(course: Course, slug: string) {
    if (this.filter().key == "masterclass") {
      this.router.navigate([
        "course",
        course.slug,
        "directory",
        "strategy",
        slug,
      ]);
    } else if (this.filter().key == "timezone") {
      this.router.navigate(["course", course.slug, "directory", "timezone"]);
    } else if (this.filter().key == "industry_id") {
      this.router.navigate([
        "course",
        course.slug,
        "directory",
        "industry",
        slug,
      ]);
    } else {
      this.router.navigate(["course", course.slug, "directory"]);
    }
  }

  goToProfile(directory: Directory): void {
    this.router.navigate([
      "course",
      this.course().slug,
      "directory",
      "profile",
      directory.id,
    ]);
  }
}
